import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  PAYMENT_METHOD, money, shortDate, type Invoice, type PaymentMethod,
} from '@lcc/shared';
import { api, qs } from '../lib/api';
import { useCurrentSeason } from '../lib/hooks';
import {
  Card, CheckField, Empty, ErrorNote, Loading, Modal, SelectField, Stat, Status, TextField,
  errorMessage, useToast,
} from '../components/ui';
import { PageHeader } from '../App';
import { StripeConnectCard } from '../components/StripeConnect';

interface Summary {
  summary: {
    invoices: number; billed_cents: number; collected_cents: number;
    outstanding_cents: number; open_invoices: number; overdue: number;
  };
  byKind: Array<{ kind: string; cents: number }>;
}

export function MoneyPage() {
  const season = useCurrentSeason();
  const [unpaidOnly, setUnpaidOnly] = useState(true);
  const [billing, setBilling] = useState(false);
  const [paying, setPaying] = useState<Invoice | null>(null);

  const summary = useQuery({
    queryKey: ['money', 'summary', season.data?.id],
    queryFn: () => api.get<Summary>(`/money/summary${qs({ seasonId: season.data?.id })}`),
    enabled: Boolean(season.data?.id),
  });

  const invoices = useQuery({
    queryKey: ['invoices', unpaidOnly],
    queryFn: () => api.get<{ data: Invoice[]; total: number }>(
      `/money/invoices${qs({ unpaidOnly: unpaidOnly || undefined, perPage: 100 })}`,
    ),
  });

  return (
    <>
      <PageHeader
        title="Invoices & fees"
        sub={season.data ? `Season ${season.data.name}` : undefined}
        actions={
          <button className="btn btn--accent" type="button" onClick={() => setBilling(true)}>
            Bill the season
          </button>
        }
      />

      <div className="content" style={{ paddingBottom: 0 }}>
        <StripeConnectCard />
      </div>

      <div className="content stack">
        <div className="grid grid--4">
          <Stat label="Billed" value={summary.data ? money(summary.data.summary.billed_cents) : '—'}
            note={summary.data ? `${summary.data.summary.invoices} invoices` : undefined} />
          <Stat label="Collected" value={summary.data ? money(summary.data.summary.collected_cents) : '—'} />
          <Stat label="Outstanding"
            value={summary.data ? money(summary.data.summary.outstanding_cents) : '—'}
            note={summary.data ? `${summary.data.summary.open_invoices} open` : undefined}
            alert={Boolean(summary.data?.summary.outstanding_cents)} />
          <Stat label="Overdue" value={summary.data?.summary.overdue ?? '—'}
            note="past the due date" alert={Boolean(summary.data?.summary.overdue)} />
        </div>

        {summary.data && summary.data.byKind.length > 0 && (
          <Card title="Billed by kind">
            <div className="row">
              {summary.data.byKind.map((k) => (
                <span key={k.kind} className="pill">
                  {k.kind}: <b>{money(k.cents)}</b>
                </span>
              ))}
            </div>
          </Card>
        )}

        <Card
          title="Invoices"
          actions={
            <CheckField label="Unpaid only" checked={unpaidOnly} onChange={setUnpaidOnly} />
          }
          flush
        >
          {invoices.isLoading ? <Loading />
            : invoices.error ? <div style={{ padding: '1rem' }}><ErrorNote error={invoices.error} /></div>
            : !invoices.data?.data.length
              ? <Empty title={unpaidOnly ? 'Nothing outstanding' : 'No invoices yet'}>
                  {unpaidOnly ? 'Every invoice is settled.' : 'Use “Bill the season” to raise membership invoices.'}
                </Empty>
              : (
                <div className="table-wrap">
                  <table className="t">
                    <thead>
                      <tr><th>#</th><th>Member</th><th>Issued</th><th>Due</th><th>For</th>
                        <th className="num">Total</th><th className="num">Paid</th>
                        <th className="num">Balance</th><th>Status</th><th /></tr>
                    </thead>
                    <tbody>
                      {invoices.data.data.map((inv) => {
                        const overdue = inv.status === 'open' && inv.dueOn
                          && inv.dueOn < new Date().toISOString().slice(0, 10);
                        return (
                          <tr key={inv.id}>
                            <td className="mono">{inv.number}</td>
                            <td>
                              {inv.memberId
                                ? <Link to={`/members/${inv.memberId}`}>{inv.memberName}</Link>
                                : inv.contactName ?? '—'}
                            </td>
                            <td className="small nowrap muted">{shortDate(inv.issuedOn)}</td>
                            <td className="small nowrap">
                              {inv.dueOn ? (
                                overdue
                                  ? <span style={{ color: 'var(--red-600)', fontWeight: 600 }}>
                                      {shortDate(inv.dueOn)}
                                    </span>
                                  : <span className="muted">{shortDate(inv.dueOn)}</span>
                              ) : '—'}
                            </td>
                            <td className="small muted">{inv.notes ?? inv.items[0]?.description ?? '—'}</td>
                            <td className="num">{money(inv.totalCents)}</td>
                            <td className="num">{money(inv.paidCents)}</td>
                            <td className="num"><b>{money(inv.balanceCents)}</b></td>
                            <td><Status value={inv.status} /></td>
                            <td className="right">
                              {inv.balanceCents > 0 && (
                                <button className="btn btn--sm btn--accent" type="button"
                                  onClick={() => setPaying(inv)}>Take payment</button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
        </Card>
      </div>

      {billing && <BillSeasonModal seasonId={season.data?.id ?? ''} onClose={() => setBilling(false)} />}
      {paying && <PaymentModal invoice={paying} onClose={() => setPaying(null)} />}
    </>
  );
}

function BillSeasonModal({ seasonId, onClose }: { seasonId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [issuedOn, setIssuedOn] = useState(new Date().toISOString().slice(0, 10));
  const [dueOn, setDueOn] = useState(
    new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10),
  );
  const [onlyUnbilled, setOnlyUnbilled] = useState(true);
  const [preview, setPreview] = useState<{
    summary: { members: number; totalCents: number };
    members: Array<{ memberId: string; fullName: string; categoryCode: string; feeCents: number }>;
  } | null>(null);

  const run = useMutation({
    mutationFn: (dryRun: boolean) => api.post<{
      dryRun?: boolean;
      summary?: { members: number; totalCents: number };
      members?: Array<{ memberId: string; fullName: string; categoryCode: string; feeCents: number }>;
      created?: number; totalCents?: number;
    }>('/money/bill-season', { seasonId, issuedOn, dueOn, onlyUnbilled, dryRun }),
    onSuccess: (r, dryRun) => {
      if (dryRun) {
        setPreview(r.summary ? { summary: r.summary, members: r.members ?? [] } : null);
        return;
      }
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['money'] });
      toast.success(`${r.created} invoices raised for ${money(r.totalCents ?? 0)}.`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <Modal
      title="Bill the season"
      wide
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn" type="button" disabled={run.isPending}
            onClick={() => run.mutate(true)}>Preview</button>
          <button className="btn btn--accent" type="button"
            disabled={run.isPending || !preview || preview.summary.members === 0}
            onClick={() => run.mutate(false)}>
            Raise {preview?.summary.members ?? ''} invoices
          </button>
        </>
      }
    >
      <div className="stack">
        {run.error ? <ErrorNote error={run.error} /> : null}
        <fieldset disabled={run.isPending}>
          <div className="grid grid--2">
            <TextField label="Issue date" type="date" value={issuedOn} onChange={setIssuedOn} />
            <TextField label="Due date" type="date" value={dueOn} onChange={setDueOn} />
          </div>
          <CheckField label="Skip members who already have a membership invoice"
            checked={onlyUnbilled} onChange={setOnlyUnbilled} />
        </fieldset>

        {preview && (
          <>
            <div className="banner banner--info">
              <strong>{preview.summary.members} members</strong> would be billed a total of{' '}
              <strong>{money(preview.summary.totalCents)}</strong>. Nothing has been raised yet.
            </div>
            <div className="table-wrap" style={{ maxHeight: 300, overflowY: 'auto' }}>
              <table className="t">
                <thead><tr><th>Member</th><th>Category</th><th className="num">Fee</th></tr></thead>
                <tbody>
                  {preview.members.map((m) => (
                    <tr key={m.memberId}>
                      <td>{m.fullName}</td>
                      <td className="muted small">{m.categoryCode}</td>
                      <td className="num">{money(m.feeCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function PaymentModal({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [amount, setAmount] = useState((invoice.balanceCents / 100).toFixed(2));
  const [method, setMethod] = useState<PaymentMethod>('etransfer');
  const [reference, setReference] = useState('');
  const [receivedOn, setReceivedOn] = useState(new Date().toISOString().slice(0, 10));

  const save = useMutation({
    mutationFn: () => api.post<{ data: Invoice }>(`/money/invoices/${invoice.id}/payments`, {
      amountCents: Math.round(Number(amount) * 100),
      method, reference: reference || null, receivedOn,
    }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['money'] });
      toast.success(r.data.status === 'paid'
        ? `Invoice #${r.data.number} settled.`
        : `Payment recorded — ${money(r.data.balanceCents)} still owing.`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <Modal
      title={`Payment — invoice #${invoice.number}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn btn--accent" type="button"
            disabled={save.isPending || !amount || Number(amount) <= 0}
            onClick={() => save.mutate()}>
            {save.isPending ? 'Recording…' : 'Record payment'}
          </button>
        </>
      }
    >
      <div className="stack">
        {save.error ? <ErrorNote error={save.error} /> : null}
        <p className="small muted" style={{ margin: 0 }}>
          {invoice.memberName ?? invoice.contactName} · balance {money(invoice.balanceCents)}
        </p>
        <div className="grid grid--2">
          <TextField label="Amount (CAD)" type="number" value={amount} onChange={setAmount} />
          <TextField label="Received on" type="date" value={receivedOn} onChange={setReceivedOn} />
        </div>
        <SelectField label="Method" value={method} onChange={setMethod}
          options={PAYMENT_METHOD.map((m) => ({ value: m, label: m }))} />
        <TextField label="Reference" value={reference} onChange={setReference}
          hint="Cheque number, e-transfer reference, or leave blank." />
      </div>
    </Modal>
  );
}
